import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { contentRichEditorSx } from "../../content/contentEditorStyles";
import { parseNewsletterCsv } from "./parseNewsletterCsv";
import { readNewsletterListFile } from "./readNewsletterListFile";

const PREVIEW_LIMIT = 20;

const embeddedTableSx = {
  width: "100%",
  maxWidth: "100%",
  tableLayout: "fixed",
};

const breakCellSx = {
  wordBreak: "break-all",
  overflowWrap: "anywhere",
};

const NewsletterCsvImportPanel = ({ csvText, onCsvTextChange, validEmails, invalidLines, duplicateCount }) => {
  const fileInputRef = useRef(null);
  const [fileLoading, setFileLoading] = useState(false);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    setFileLoading(true);
    try {
      const text = await readNewsletterListFile(file);
      onCsvTextChange(text || "");
    } catch {
      onCsvTextChange("");
    } finally {
      setFileLoading(false);
    }
  };

  const previewEmails = validEmails.slice(0, PREVIEW_LIMIT);

  return (
    <Stack spacing={2} sx={{ minWidth: 0, width: "100%", maxWidth: "100%" }}>
      <Typography variant="body2" color="text.secondary">
        Carica un file CSV, Excel (.xlsx, .xls) o incolla una email per riga. Sono accettate anche righe con separatore
        virgola o punto e virgola (viene usata la prima colonna).
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={fileLoading}
          onClick={() => fileInputRef.current?.click()}
        >
          {fileLoading ? "Caricamento..." : "Carica file"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          hidden
          onChange={handleFileChange}
        />
      </Box>

      <Box
        component="textarea"
        value={csvText}
        onChange={(e) => onCsvTextChange(e.target.value)}
        placeholder="email1@example.com&#10;email2@example.com"
        sx={{
          ...contentRichEditorSx,
          display: "block",
          width: "100%",
          minHeight: 160,
          fontFamily: "monospace",
          fontSize: 13,
          resize: "vertical",
        }}
      />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip label={`${validEmails.length} email valide`} color="success" variant="outlined" size="small" />
        {invalidLines.length ? (
          <Chip label={`${invalidLines.length} righe non valide`} color="warning" variant="outlined" size="small" />
        ) : null}
        {duplicateCount > 0 ? (
          <Chip label={`${duplicateCount} duplicati ignorati`} variant="outlined" size="small" />
        ) : null}
      </Box>

      {invalidLines.length ? (
        <Alert severity="warning">
          Alcune righe non sono state importate. Controlla il formato email nelle righe evidenziate.
        </Alert>
      ) : null}

      {previewEmails.length ? (
        <TableContainer sx={{ width: "100%", maxWidth: "100%", border: "1px solid #e2e8f0", borderRadius: 1, maxHeight: 240, overflow: "hidden" }}>
          <Table size="small" stickyHeader sx={embeddedTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Anteprima email valide</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewEmails.map((email) => (
                <TableRow key={email}>
                  <TableCell sx={breakCellSx}>{email}</TableCell>
                </TableRow>
              ))}
              {validEmails.length > PREVIEW_LIMIT ? (
                <TableRow>
                  <TableCell sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    ... e altre {validEmails.length - PREVIEW_LIMIT} email
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {invalidLines.length ? (
        <TableContainer sx={{ width: "100%", maxWidth: "100%", border: "1px solid #fecaca", borderRadius: 1, maxHeight: 200, overflow: "hidden" }}>
          <Table size="small" sx={embeddedTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "12%" }}>Riga</TableCell>
                <TableCell sx={{ width: "44%" }}>Contenuto</TableCell>
                <TableCell sx={{ width: "44%" }}>Motivo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invalidLines.slice(0, 10).map((item) => (
                <TableRow key={`${item.line}-${item.value}`}>
                  <TableCell>{item.line}</TableCell>
                  <TableCell sx={breakCellSx}>{item.value}</TableCell>
                  <TableCell sx={breakCellSx}>{item.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Stack>
  );
};

NewsletterCsvImportPanel.propTypes = {
  csvText: PropTypes.string,
  onCsvTextChange: PropTypes.func.isRequired,
  validEmails: PropTypes.arrayOf(PropTypes.string),
  invalidLines: PropTypes.arrayOf(
    PropTypes.shape({
      line: PropTypes.number,
      value: PropTypes.string,
      reason: PropTypes.string,
    })
  ),
  duplicateCount: PropTypes.number,
};

export { parseNewsletterCsv };
export default NewsletterCsvImportPanel;
